import React, { useState } from 'react';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Textarea } from './ui/textarea';
import { Label } from './ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { toast } from 'sonner';
import axios from 'axios';
import { initiatePayment, getPackageAmount } from '../utils/razorpay';
import { ChevronLeft, ChevronRight } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const BookingForm = () => {
  const [currentStep, setCurrentStep] = useState(1);
  const [formData, setFormData] = useState({
    fullName: '',
    phone: '',
    email: '',
    gym: '',
    package: '',
    date: '',
    time: '',
    message: ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handlePackageChange = (value) => {
    setFormData(prev => ({ ...prev, package: value }));
  };

  const validateStep1 = () => {
    if (!formData.fullName || !formData.phone || !formData.gym) {
      toast.error('Please fill in all required fields');
      return false;
    }
    return true;
  };

  const validateStep2 = () => {
    if (!formData.package || !formData.date) {
      toast.error('Please select package and shoot date');
      return false;
    }
    return true;
  };

  const handleNext = () => {
    if (currentStep === 1 && !validateStep1()) return;
    if (currentStep === 2 && !validateStep2()) return;
    setCurrentStep(prev => prev + 1);
  };

  const handleBack = () => {
    setCurrentStep(prev => prev - 1);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    setIsSubmitting(true);
    
    try {
      const bookingResponse = await axios.post(`${BACKEND_URL}/api/bookings/`, formData);
      
      if (!bookingResponse.data.success) {
        throw new Error('Failed to create booking');
      }

      const bookingId = bookingResponse.data.bookingId;
      const amount = getPackageAmount(formData.package);

      const orderResponse = await axios.post(`${BACKEND_URL}/api/payment/create-order`, {
        amount: amount,
        currency: 'INR',
        receipt: `FITFRAME_${bookingId.substring(0, 10)}`,
        notes: {
          bookingId: bookingId,
          package: formData.package,
          customerName: formData.fullName
        }
      });

      if (!orderResponse.data.success) {
        throw new Error('Failed to create payment order');
      }

      await initiatePayment({
        amount: amount,
        orderId: orderResponse.data.order_id,
        customerName: formData.fullName,
        customerPhone: formData.phone,
        customerEmail: formData.email,
        onSuccess: async (response) => {
          try {
            const verifyResponse = await axios.post(`${BACKEND_URL}/api/payment/verify`, {
              razorpay_order_id: response.razorpay_order_id,
              razorpay_payment_id: response.razorpay_payment_id,
              razorpay_signature: response.razorpay_signature,
              booking_id: bookingId
            });

            if (verifyResponse.data.success) {
              await axios.patch(
                `${BACKEND_URL}/api/bookings/${bookingId}/payment?payment_id=${response.razorpay_payment_id}&order_id=${response.razorpay_order_id}&amount=${amount}&status=paid`
              );

              toast.success('🎉 Payment successful! Your shoot is confirmed. We\'ll contact you soon!');
              
              setFormData({
                fullName: '',
                phone: '',
                email: '',
                gym: '',
                package: '',
                date: '',
                time: '',
                message: ''
              });
              setCurrentStep(1);
            }
          } catch (error) {
            console.error('Payment verification error:', error);
            toast.error('Payment completed but verification failed. Please contact support.');
          } finally {
            setIsSubmitting(false);
          }
        },
        onFailure: (error) => {
          console.error('Payment failed:', error);
          toast.error('Payment failed or cancelled. Please try again.');
          setIsSubmitting(false);
        }
      });

    } catch (error) {
      console.error('Booking submission error:', error);
      toast.error(error.response?.data?.detail || 'Something went wrong. Please try again.');
      setIsSubmitting(false);
    }
  };

  return (
    <section id="booking" className="relative bg-[#0d0d0d] py-20 md:py-32 overflow-hidden">
      {/* Background Pattern */}
      <div className="absolute inset-0 opacity-5">
        <div className="absolute inset-0" style={{
          backgroundImage: `repeating-linear-gradient(
            45deg,
            transparent,
            transparent 35px,
            rgba(232, 255, 71, 0.1) 35px,
            rgba(232, 255, 71, 0.1) 70px
          )`
        }}></div>
      </div>

      <div className="relative z-10 max-w-4xl mx-auto px-5 md:px-10">
        {/* Section Header */}
        <div className="text-center mb-12">
          <h2 className="font-['Bebas_Neue'] text-white text-5xl md:text-7xl tracking-wide mb-3">
            BOOK YOUR <span className="text-[#e8ff47]">SHOOT</span>
          </h2>
          <p className="font-['DM_Sans'] text-[#888680] text-base">
            2 minutes to fill. A lifetime of results.
          </p>
        </div>

        {/* Step Indicator */}
        <div className="mb-8">
          <div className="flex items-center justify-center gap-2 mb-4">
            <span className="font-['Bebas_Neue'] text-[#e8ff47] text-2xl">
              STEP {currentStep} OF 3
            </span>
          </div>
          <div className="flex gap-2 max-w-md mx-auto">
            <div className={`h-2 flex-1 rounded-full transition-all ${currentStep >= 1 ? 'bg-[#e8ff47]' : 'bg-[#3f4816]'}`}></div>
            <div className={`h-2 flex-1 rounded-full transition-all ${currentStep >= 2 ? 'bg-[#e8ff47]' : 'bg-[#3f4816]'}`}></div>
            <div className={`h-2 flex-1 rounded-full transition-all ${currentStep >= 3 ? 'bg-[#e8ff47]' : 'bg-[#3f4816]'}`}></div>
          </div>
        </div>

        {/* Form */}
        <form onSubmit={handleSubmit} className="bg-[#302f2c] border-2 border-[#3f4816] rounded-lg p-8 md:p-12">
          
          {/* STEP 1: Know You Better */}
          {currentStep === 1 && (
            <div className="space-y-6">
              <h3 className="font-['Bebas_Neue'] text-[#e8ff47] text-3xl mb-6 text-center">
                KNOW YOU BETTER
              </h3>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                {/* Full Name */}
                <div className="space-y-2">
                  <Label className="font-['DM_Sans'] text-[#e8ff47] text-xs font-bold uppercase tracking-wider">
                    Full Name *
                  </Label>
                  <Input
                    name="fullName"
                    value={formData.fullName}
                    onChange={handleInputChange}
                    className="bg-[#0d0d0d] border-0 border-b-2 border-[#e8ff47] rounded-none text-[#dfddd6] text-lg px-0 py-3"
                    placeholder="Your name"
                    required
                  />
                </div>

                {/* Phone */}
                <div className="space-y-2">
                  <Label className="font-['DM_Sans'] text-[#e8ff47] text-xs font-bold uppercase tracking-wider">
                    Phone Number *
                  </Label>
                  <Input
                    name="phone"
                    type="tel"
                    value={formData.phone}
                    onChange={handleInputChange}
                    className="bg-[#0d0d0d] border-0 border-b-2 border-[#e8ff47] rounded-none text-[#dfddd6] text-lg px-0 py-3"
                    placeholder="Your phone"
                    required
                  />
                </div>

                {/* Email */}
                <div className="space-y-2">
                  <Label className="font-['DM_Sans'] text-[#e8ff47] text-xs font-bold uppercase tracking-wider">
                    Email ID
                  </Label>
                  <Input
                    name="email"
                    type="email"
                    value={formData.email}
                    onChange={handleInputChange}
                    className="bg-[#0d0d0d] border-0 border-b-2 border-[#888680] rounded-none text-[#dfddd6] text-lg px-0 py-3"
                    placeholder="Your email (optional)"
                  />
                </div>

                {/* Gym */}
                <div className="space-y-2">
                  <Label className="font-['DM_Sans'] text-[#e8ff47] text-xs font-bold uppercase tracking-wider">
                    Which Gym? *
                  </Label>
                  <Input
                    name="gym"
                    value={formData.gym}
                    onChange={handleInputChange}
                    className="bg-[#0d0d0d] border-0 border-b-2 border-[#e8ff47] rounded-none text-[#dfddd6] text-lg px-0 py-3"
                    placeholder="Your gym name"
                    required
                  />
                </div>
              </div>

              <Button 
                type="button"
                onClick={handleNext}
                className="w-full bg-[#e8ff47] text-[#0d0d0d] hover:bg-[#d9eb3d] rounded-full py-6 font-['Bebas_Neue'] text-xl uppercase mt-8"
              >
                Next <ChevronRight className="ml-2 w-5 h-5" />
              </Button>
            </div>
          )}

          {/* STEP 2: Execute Better */}
          {currentStep === 2 && (
            <div className="space-y-6">
              <h3 className="font-['Bebas_Neue'] text-[#e8ff47] text-3xl mb-6 text-center">
                EXECUTE BETTER
              </h3>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                {/* Package */}
                <div className="space-y-2 md:col-span-2">
                  <Label className="font-['DM_Sans'] text-[#e8ff47] text-xs font-bold uppercase tracking-wider">
                    Select Package *
                  </Label>
                  <Select onValueChange={handlePackageChange} value={formData.package}>
                    <SelectTrigger className="bg-[#0d0d0d] border-0 border-b-2 border-[#e8ff47] rounded-none text-[#dfddd6] text-lg px-0 py-3">
                      <SelectValue placeholder="Choose your package" />
                    </SelectTrigger>
                    <SelectContent className="bg-[#0d0d0d] border-[#3f4816]">
                      <SelectItem value="starter" className="text-[#dfddd6]">Starter - ₹1,499</SelectItem>
                      <SelectItem value="pro" className="text-[#dfddd6]">Pro - ₹2,199</SelectItem>
                      <SelectItem value="transformation" className="text-[#dfddd6]">Transformation - ₹4,999</SelectItem>
                    </SelectContent>
                  </Select>
                </div>

                {/* Date */}
                <div className="space-y-2">
                  <Label className="font-['DM_Sans'] text-[#e8ff47] text-xs font-bold uppercase tracking-wider">
                    Shoot Date *
                  </Label>
                  <Input
                    name="date"
                    type="date"
                    value={formData.date}
                    onChange={handleInputChange}
                    className="bg-[#0d0d0d] border-0 border-b-2 border-[#e8ff47] rounded-none text-[#dfddd6] text-lg px-0 py-3"
                    required
                  />
                </div>

                {/* Time */}
                <div className="space-y-2">
                  <Label className="font-['DM_Sans'] text-[#e8ff47] text-xs font-bold uppercase tracking-wider">
                    Start Time
                  </Label>
                  <Input
                    name="time"
                    type="time"
                    value={formData.time}
                    onChange={handleInputChange}
                    className="bg-[#0d0d0d] border-0 border-b-2 border-[#888680] rounded-none text-[#dfddd6] text-lg px-0 py-3"
                  />
                  <p className="text-[#888680] text-xs mt-1">We'll guide you on the best time</p>
                </div>
              </div>

              <div className="flex gap-4 mt-8">
                <Button 
                  type="button"
                  onClick={handleBack}
                  className="flex-1 bg-[#3f4816] text-[#dfddd6] hover:bg-[#4a5520] rounded-full py-6 font-['Bebas_Neue'] text-xl uppercase"
                >
                  <ChevronLeft className="mr-2 w-5 h-5" /> Back
                </Button>
                <Button 
                  type="button"
                  onClick={handleNext}
                  className="flex-1 bg-[#e8ff47] text-[#0d0d0d] hover:bg-[#d9eb3d] rounded-full py-6 font-['Bebas_Neue'] text-xl uppercase"
                >
                  Next <ChevronRight className="ml-2 w-5 h-5" />
                </Button>
              </div>
            </div>
          )}

          {/* STEP 3: Final Touch */}
          {currentStep === 3 && (
            <div className="space-y-6">
              <h3 className="font-['Bebas_Neue'] text-[#e8ff47] text-3xl mb-6 text-center">
                FINAL TOUCH
              </h3>

              <div className="space-y-2">
                <Label className="font-['DM_Sans'] text-[#e8ff47] text-xs font-bold uppercase tracking-wider">
                  Any Specific Thoughts?
                </Label>
                <Textarea
                  name="message"
                  value={formData.message}
                  onChange={handleInputChange}
                  className="bg-[#0d0d0d] border-2 border-[#888680] text-[#dfddd6] min-h-32 p-4 rounded-md focus:border-[#e8ff47]"
                  placeholder="Share any requirements or questions (optional)"
                />
              </div>

              <div className="space-y-4 mt-8">
                <Button 
                  type="submit"
                  disabled={isSubmitting}
                  className="w-full bg-[#e8ff47] text-[#0d0d0d] hover:bg-[#d9eb3d] rounded-full py-7 font-['Bebas_Neue'] text-2xl uppercase shadow-lg hover:scale-105 transition-all"
                >
                  {isSubmitting ? 'Processing...' : 'BOOK MY SHOOT NOW'}
                </Button>
                
                <p className="text-[#888680] text-sm text-center">
                  We'll confirm your shoot within 24 hours
                </p>

                <Button 
                  type="button"
                  onClick={handleBack}
                  className="w-full bg-transparent border border-[#3f4816] text-[#dfddd6] hover:bg-[#3f4816] rounded-full py-4 font-['DM_Sans'] text-base uppercase"
                >
                  <ChevronLeft className="mr-2 w-4 h-4" /> Back to Previous Step
                </Button>
              </div>
            </div>
          )}

        </form>
      </div>
    </section>
  );
};

export default BookingForm;